<script setup>
import {ref, watch, onMounted, onUnmounted} from 'vue';
import {useRouter} from 'vue-router';
import {ApiWrapper} from "@/Utils/ApiWrapper.js";
import {EventStream} from "@/Utils/EventStream.js";
import {activeConversationId} from "@/Utils/ActiveConversation.js";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const PAGE_SIZE = 4;
const POLL_INTERVAL_MS = 30 * 1000; // only used while the stream isn't connected

const router = useRouter();

const menuOpen = ref(false);
const loading = ref(true);
const notifications = ref([]);
const unreadCount = ref(0); // from the server, so it covers all notifications, not just loaded ones
const nextCursor = ref(null);
const hasMore = ref(false);
const listKey = ref(0); // bumped to reset the infinite scroll after a fresh load

let pollIntervalId = null;
let offStreamHandlers = [];
let newestSeenId = 0;    // newest notification id fetched, including ones that weren't shown

async function getNotifications(params = {}) {
    const query = new URLSearchParams({limit: PAGE_SIZE, ...params}).toString();
    const response = await ApiWrapper.get('notifications', 'get-notifications', query);

    unreadCount.value = response.unreadCount;

    return response;
}

// Replace the list with the newest page
async function loadInitial() {
    try {
        const response = await getNotifications();

        notifications.value = response.notifications;
        newestSeenId = response.notifications[0]?.id ?? 0;
        nextCursor.value = response.nextCursor;
        hasMore.value = response.hasMore;
        listKey.value++;
    }
    catch (error) {
        console.error('Error loading notifications', error);
    }
    finally {
        loading.value = false;
    }
}

async function loadMore({done}) {
    if (!hasMore.value) {
        done('empty');
        return;
    }

    try {
        const response = await getNotifications({before: nextCursor.value});

        const loadedIds = new Set(notifications.value.map((n) => n.id));
        notifications.value.push(...response.notifications.filter((n) => !loadedIds.has(n.id)));
        nextCursor.value = response.nextCursor;
        hasMore.value = response.hasMore;

        done(response.hasMore ? 'ok' : 'empty');
    }
    catch (error) {
        console.error('Error loading more notifications', error);
        done('error');
    }
}

// New notifications about the conversation they have open aren't needed, they can see the message.
// Mark those read on the server and leave them out, and take them off the unread count.
function leaveOutOpenConversation(response) {
    if (document.hidden || !activeConversationId.value) return response.notifications;

    const aboutOpenConversation = response.notifications.filter(
        (n) => n.entity_type === 'conversation' && n.entity_id === activeConversationId.value
    );
    if (aboutOpenConversation.length === 0) return response.notifications;

    const unread = aboutOpenConversation.filter((n) => !n.read_at);
    unread.forEach((n) => {
        ApiWrapper.post('notifications', 'mark-read', {id: n.id})
            .catch((error) => console.error('Error marking notification read', error));
    });
    unreadCount.value = Math.max(response.unreadCount - unread.length, 0);

    return response.notifications.filter((n) => !aboutOpenConversation.includes(n));
}

// Prepend anything newer than what's loaded
async function pollNew() {

    // nothing has been loaded yet, initial load
    if (newestSeenId === 0) {
        return loadInitial();
    }

    try {
        const response = await getNotifications({after: newestSeenId});

        // More than a page has arrived since the last poll; start over rather than leave a gap
        if (response.hasMore) {
            return loadInitial();
        }

        newestSeenId = Math.max(newestSeenId, ...response.notifications.map((n) => n.id));

        const toShow = leaveOutOpenConversation(response);

        const loadedIds = new Set(notifications.value.map((n) => n.id));
        notifications.value.unshift(...toShow.filter((n) => !loadedIds.has(n.id)));
    } catch (error) {
        console.error('Error polling notifications', error);
    }
}

// Update the UI right away, then undo it if the request fails
async function markRead(notification) {
    if (notification.read_at) return;

    notification.read_at = new Date().toISOString();
    unreadCount.value = Math.max(unreadCount.value - 1, 0);

    try {
        await ApiWrapper.post('notifications', 'mark-read', {
            id: notification.id,
        });
    } catch (error) {
        console.error('Error marking notification read', error);
        notification.read_at = null;
        unreadCount.value++;
    }
}

async function markAllRead() {
    const unread = notifications.value.filter((n) => !n.read_at);
    const previousUnreadCount = unreadCount.value;
    const now = new Date().toISOString();

    unread.forEach((n) => n.read_at = now);
    unreadCount.value = 0;

    try {
        await ApiWrapper.post('notifications', 'mark-read', {
            all: true,
        });
    } catch (error) {
        console.error('Error marking all notifications read', error);
        unread.forEach((n) => n.read_at = null);
        unreadCount.value = previousUnreadCount;
    }
}

function onNotificationClick(notification) {
    markRead(notification);
    menuOpen.value = false;

    if (notification.link) {
        router.push(notification.link);
    }
}

watch(menuOpen, (open) => {
    if (open) pollNew();
});

// Server-sent events: the server only signals that something changed, then we fetch with pollNew()
onMounted(() => {
    loadInitial();

    // The server pushes a signal when there's something new; after a reconnect, catch up on anything missed
    offStreamHandlers = [
        EventStream.on('notification', () => pollNew()),
        EventStream.on('resync', () => pollNew()),
        EventStream.on('open', ({reconnected}) => {
            if (reconnected) pollNew();
        }),
    ];

    // Poll while the stream is disabled or reconnecting
    pollIntervalId = setInterval(() => {
        if (!EventStream.connected.value && !document.hidden) pollNew();
    }, POLL_INTERVAL_MS);
});

onUnmounted(() => {
    clearInterval(pollIntervalId);
    offStreamHandlers.forEach((off) => off());
});
</script>


<template>
    <v-menu
        v-model="menuOpen"
        :close-on-content-click="false"
        location="bottom end"
        width="380"
        max-height="480"
    >

        <template #activator="{ props }">
            <v-btn icon size='large' v-bind="props">
                <v-badge
                    :content="unreadCount"
                    :model-value="unreadCount > 0"
                    color="error"
                >
                    <v-icon>mdi-bell</v-icon>
                </v-badge>
            </v-btn>
        </template>

        <!-- Card fills the menu's height cap so only the infinite scroll scrolls, never the menu -->
        <v-card class="d-flex flex-column" style="max-height: inherit">
            <v-card-title class="text-subtitle-1 d-flex align-center justify-space-between">
                Notifications
                <v-btn
                    v-if="unreadCount > 0"
                    size="small"
                    variant="text"
                    @click="markAllRead"
                >
                    Mark all read
                </v-btn>
            </v-card-title>

            <v-divider/>

            <div v-if="loading" class="d-flex justify-center pa-4">
                <v-progress-circular indeterminate size="24"/>
            </div>

            <div
                v-else-if="notifications.length === 0"
                class="text-body-2 text-medium-emphasis text-center pa-6"
            >
                No notifications yet
            </div>

            <v-infinite-scroll
                v-else
                :key="listKey"
                style="min-height: 0"
                @load="loadMore"
            >
                <div
                    v-for="notification in notifications"
                    :key="notification.id"
                    v-ripple
                    class="notification"
                    :class="{ 'notification--unread': !notification.read_at }"
                    role="button"
                    tabindex="0"
                    @click="onNotificationClick(notification)"
                    @keydown.enter="onNotificationClick(notification)"
                >
                    <div class="notification__content">
                        <div class="text-body-2" :class="{ 'font-weight-bold': !notification.read_at }">
                            {{ notification.title }}
                        </div>
                        <!-- Plain text only -->
                        <div v-if="notification.body" class="notification__body text-body-2 text-medium-emphasis">
                            {{ notification.body }}
                        </div>
                        <div
                            class="text-caption"
                            :class="notification.read_at ? 'text-medium-emphasis' : 'text-primary font-weight-medium'"
                        >
                            {{ dayjs(notification.created_at).fromNow() }}
                        </div>
                    </div>

                    <v-icon
                        v-if="!notification.read_at"
                        color="primary"
                        size="10"
                        icon="mdi-circle"
                    />
                </div>

                <template #empty/>
            </v-infinite-scroll>
        </v-card>
    </v-menu>
</template>

<style scoped>
.notification {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    cursor: pointer;
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.notification:hover {
    background: rgba(var(--v-theme-on-surface), 0.04);
}

.notification--unread {
    background: rgba(var(--v-theme-primary), 0.06);
}

.notification__content {
    flex: 1;
    min-width: 0;
}

.notification__body {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
